import { GuideOptions } from '@editor/fabric/shapes/Guide/types';
import { IllustrationSerializedChildren } from '@editor/fabric/shapes/Illustration/types';
import type { PathTextInitOptions } from '@editor/fabric/shapes/PathText/types';
import { DecorationOptions } from '@editor/global/styling/decoration';
import { LayerIconType } from '@editor/types';
import { AssetInstanceObjectProperties } from '@editor/types/LibraryAssets';
import { fabric } from 'fabric';
import {
  CombinedShapeOptions,
  IllustrationImageOptions,
  IllustrationOptions,
} from 'fabric/fabric-impl';
import { ColorFormats } from 'tinycolor2';

import type {
  ImageFilterType,
  SerialisedPhotoFilter,
} from '../global/styling/photoFilter';
import type { Color, GradientObject } from '../utils/color';
import type { StructureObject } from '../utils/groupStructure';

import type { LayoutConfig } from './Artboard';
import { ObjectType, SerializableObjectType } from './fabric';
import type { LinkedPreset } from './LinkedPreset';

/**
 * ColorInfo interface. This is how colors
 * are communicated to UI or saved to state.
 *
 * NOTE: GradientObject never makes it to saved state
 */

export const stringColorKeys = ['stroke', 'shadow', 'decoration'] as const;

type StringColorKeysType = (typeof stringColorKeys)[number];

export type StringColor = {
  // TODO: Arbitrary color key strings may come from colors in SVG files (e.g. 'color-0-0', etc).
  // {} is a hint for a typescript to support string constants and any string
  // eslint-disable-next-line @typescript-eslint/ban-types
  key: StringColorKeysType | (string & {});
  value: string | null;
  visible: boolean;
};

// what is better name for it?
export type FullColor = {
  // TODO: Arbitrary color key strings may come from colors in SVG files (e.g. 'color-0-0', etc).
  // {} is a hint for a typescript to support string constants and any string
  // eslint-disable-next-line @typescript-eslint/ban-types
  key: 'fill' | (string & {});
  value: Color;
  visible: boolean;
};

export type ColorInfo = StringColor | FullColor;

export enum BoardLayout {
  Custom = 'custom',
  Standard = 'standard',
  TShirt = 'tshirt',
  BusinessCard = 'businesscard',
  A4 = 'a4',
  A5 = 'a5',
  Poster = 'poster',
  InstagramPost = 'instagramPost',
  InstagramPostPortrait = 'instagramPostPortrait',
  instagramPostSquare = 'instagramPostSquare',
  InstagramPostLandscape = 'instagramPostLandscape',
  InstagramStory = 'instagramStory',
  InstagramReel = 'instagramReel',
  FacebookPost = 'facebookPost',
  FacebookPostSquare = 'facebookPostSquare',
  FacebookPostLandscape = 'facebookPostLandscape',
  FacebookStory = 'facebookStory',
  YouTubeThumbnail = 'youtubeThumbnail',
  YouTubeProfileBanner = 'youtubeProfileBanner',
  YouTubeAdBanner = 'youtubeAdBanner',
  XPost = 'xPost',
  XHeader = 'xHeader',
  XCarouselAd = 'xCarouselAd',
  PinterestPin = 'pinterestPin',
  WebBannerAd = 'webBannerAd',
  Postcard = 'postcard',
  Letterhead = 'letterhead',
  Flyer = 'flyer',
  Brochure = 'brochure',
  Logo = 'logo',
  Presentation = 'presentation',
  AmazonTShirt = 'amazonTShirt',
  PrintfulTShirt = 'printfulTShirt',
  PrintfulAop = 'printfulAop',
  PrintfulHat = 'printfulHat',
  RedBubbleStandard = 'redBubbleStandard',
  RedBubbleSticker = 'redBubbleSticker',
  TeepublicStandard = 'teepublicStandard',
  DisplatePoster = 'displatePoster',
  PrintifyStandard = 'printifyStandard',
  PrintifyAop = 'printifyAop',
  PrintifyHoodie = 'printifyHoodie',
  PrintifyMug = 'printifyMug',
  PrintifyNotebook = 'printifyNotebook',
  PrintifySticker = 'printifySticker',
  AwkwardStylesTShirt = 'awkwardStylesTShirt',
}

export type Layouts = Partial<Record<BoardLayout, LayoutConfig>>;

export enum Unit {
  px = 'px',
  in = 'in',
  mm = 'mm',
}

export enum BlendMode {
  // Normal
  Normal = 'normal',
  // Standard
  Multiply = 'multiply',
  Screen = 'screen',
  Overlay = 'overlay',
  // Lighten and shadows
  SoftLight = 'soft-light',
  HardLight = 'hard-light',
  ColorDodge = 'color-dodge',
  ColorBurn = 'color-burn',
  // Contrast
  Lighten = 'lighten',
  Darken = 'darken',
  Difference = 'difference',
  Exclusion = 'exclusion',
  Lighter = 'lighter',
  // Color properties
  Hue = 'hue',
  Saturation = 'saturation',
  Color = 'color',
  Luminosity = 'luminosity',
  // Special
  AlphaMask = 'destination-out',
}

export interface ColorPaletteItemLinkedObject {
  id: string;
  key: string;
  visible: boolean;
  color: null | string | GradientObject | ColorFormats.HSLA;
  type: ObjectType;
}

export type ColorPalette = Map<string, ColorPaletteItemLinkedObject[]>;

export interface BackgroundImageOptions {
  objectName: string;
  globalCompositeOperation: BlendMode;
  opacity: number;
  filters: (fabric.IBaseFilter & { type: ImageFilterType })[];
  hidden: boolean;
  cropX?: number;
  cropY?: number;
  scaleX?: number;
  scaleY?: number;
  width?: number;
  height?: number;
  realWidth?: number;
  realHeight?: number;
}

export interface ArtboardOptions extends fabric.IRectOptions {
  id?: string;
  lockMovementX?: boolean;
  lockMovementY?: boolean;
  activeLayout: BoardLayout;
  left?: number;
  top?: number;
  width: number;
  height: number;
  fill: string | fabric.Gradient | undefined;
  opacity?: number;
  portrait?: boolean;
  /** Whether the artboard is visible */
  hidden?: boolean;
  /**
   * @deprecated will be per-project
   */
  unit?: Unit;
  /**
   * @deprecated will be per-project
   */
  dpi?: number;
  fullSize?: boolean;
  clip?: boolean;
  presets?: LinkedPreset[];
  canvas?: fabric.Canvas /*| undefined*/;
  printMargins?: PrintMargins;
  scaleX?: number;
  scaleY?: number;
}

/**
 * Before multiple artboards, we had background image options inside artboard options.
 */
export interface ArtboardOptionsWithBackgroundImage extends ArtboardOptions {
  backgroundImage?: BackgroundImageOptions | null;
  title?: string;
}

export interface TextureOptions {
  texture: string;
  mode: BlendMode;
  opacity: number;
  hidden?: boolean;
  loaded?: boolean;
  isAlphaMask?: boolean;
  renderClip?: boolean;
}

export interface DesignConfig {
  title?: string;
  dpiVersion?: number;
  designId?: string;
  generationId?: string;
  updatedAt?: string;
  // Note: templateId still can be number for old designs
  templateId?: number | string;
  folderId?: string;
  dpi?: number;
  unit?: Unit;
  overrideColorPalette?: string[];
  overrideColorKeys?: string[];
  overrideLuminosityRatio?: number;
  previewBoardId?: string;
  printBleed?: PrintBleed;
}

export type PrintMargins = {
  enabled: boolean;
  top: number;
  right: number;
  bottom: number;
  left: number;
};

export type BleedDefinition = { value: number; unit: Unit };

export type BleedOption = BleedDefinition & { label: string };

export type PrintBleed = BleedDefinition & { enabled: boolean };

export type ObjectTransformation = [
  number,
  number,
  number,
  number,
  number,
  number,
];

export type DesignObjectId = string;

export interface DesignStateBase {
  config: DesignConfig;
}

export interface BaseSerializedObject {
  id: DesignObjectId;
  version?: string;
  asset?: AssetInstanceObjectProperties;
  name?: string;
  locked?: boolean;
  hidden?: boolean;
  opacity?: number;
  presets?: LinkedPreset[];
  defaultName?: string;
  /**
   * The default icon to be used in the layers panel to represent the element in the canvas.
   */
  defaultIcon?: LayerIconType;
  objectName?: string;
  angle: number;
  left: number;
  top: number;
  width: number;
  height: number;
  scaleX: number;
  scaleY: number;
  flipX?: boolean;
  flipY?: boolean;
  stroke?: Color;
  skewX?: number;
  skewY?: number;
  strokeWidth?: number;
  // TODO: Validate startAngle and endAngle should indeed live here
  /**
   * Start angle of the circle, moving clockwise
   */
  startAngle?: number | undefined;
  /**
   * End angle of the circle
   */
  endAngle?: number | undefined;
  decorationOptions?: DecorationOptions;

  // TODO: split additional options even further
  shadowOptions?: fabric.IShadowOptions & {
    offset?: number;
    angle?: number;
    outlineWidth?: number;
  };
  filters?: SerialisedPhotoFilter[];
  hiddenColors?: Record<string, NonNullable<Color>>;
  fill?: Color | ColorInfo[];
  shadow?: string | null;
  decoration?: string | null;

  [key: string]: unknown;
}

export type IllustrationSerializedObject = {
  type: ObjectType.Illustration;
  childrenInfo?: fabric.IllustrationOptions['childrenInfo'];
} & IllustrationOptions;

export type BasicShapeSerializedObject = {
  type: ObjectType.BasicShape;
  childrenInfo?: fabric.IllustrationOptions['childrenInfo'];
} & IllustrationOptions;

export type IllustrationImageSerializedObject = {
  type: ObjectType.IllustrationImage;
} & BackgroundImageOptions &
  IllustrationImageOptions;

export type CombinedShapeSerializedObject = {
  type: ObjectType.CombinedShape;
  childrenInfo?: fabric.IllustrationOptions['childrenInfo'];
} & CombinedShapeOptions;

export type OverlayTextureSerializedObject = {
  type: ObjectType.OverlayTexture;
} & TextureOptions;

export type PathOptions = {
  pathOffset: {
    x: number;
    y: number;
  };
};

export type PathSerializedObject = {
  type: ObjectType.Path;
} & PathOptions;

export type GuideSerializedObject = {
  id: DesignObjectId;
  version?: string;
  type: ObjectType.Guide;
  top?: number;
  left?: number;
} & GuideOptions;

export type PathTextSerializedObject = BaseSerializedObject &
  PathTextInitOptions;

export type ArtboardSerializedObject = ArtboardOptionsWithBackgroundImage & {
  type: ObjectType.Artboard;
};

export type StandardSerializedObject =
  | (BaseSerializedObject & {
      type: Exclude<
        SerializableObjectType,
        | ObjectType.PathText
        | ObjectType.IllustrationImage
        | ObjectType.CombinedShape
        | ObjectType.OverlayTexture
        | ObjectType.Path
        | ObjectType.Guide
      >;
    })
  // Objects exposing BaseSerializedObject and custom properties
  | PathTextSerializedObject
  | (BaseSerializedObject &
      (
        | IllustrationImageSerializedObject
        | CombinedShapeSerializedObject
        | OverlayTextureSerializedObject
        | PathSerializedObject
        | IllustrationSerializedObject
        | BasicShapeSerializedObject
      ));

export type SerializedObject =
  | StandardSerializedObject
  // Guide doesn't have BaseSerializedObject-properties
  | GuideSerializedObject;
/**
 * An extended serialized object that contains a children property.
 * This is used in UnfoldedDesignState where we need direct access to children.
 * For example, the layers panel needs to know what's inside an illustration,
 * but we don't include this in StandardSerializedObject because children
 * should never be saved as part of a project (check childrenInfo).
 */
export type SerializedObjectWithChildren = StandardSerializedObject & {
  children?: IllustrationSerializedChildren | null;
};

export function isSerializedObjectWithChildren(
  object: SerializedObject
): object is SerializedObjectWithChildren {
  return 'children' in object;
}

export function isStandardSerializedObject(
  object: SerializedObject
): object is StandardSerializedObject {
  return object.type !== ObjectType.Guide;
}

export function isPartialStandardSerializedObject(
  object: Partial<SerializedObject>
): object is Partial<StandardSerializedObject> {
  return object.type !== ObjectType.Guide;
}

export function isPathTextSerializedObject(
  object: SerializedObject
): object is PathTextSerializedObject {
  return object.type === ObjectType.PathText;
}

export function isCombinedShapeSerializedObject(
  object: SerializedObject
): object is BaseSerializedObject & CombinedShapeSerializedObject {
  return object.type === ObjectType.CombinedShape;
}

export function isPathSerializedObject(
  object: SerializedObject
): object is BaseSerializedObject & PathSerializedObject {
  return object.type === ObjectType.Path;
}

export function isIllustrationImageSerializedObject(
  object: SerializedObject
): object is BaseSerializedObject & IllustrationImageSerializedObject {
  return object.type === ObjectType.IllustrationImage;
}

export function isIllustrationSerializedObject(
  object: SerializedObject
): object is BaseSerializedObject & IllustrationSerializedObject {
  return object.type === ObjectType.Illustration;
}

export function isVectorBasedSerializedObject(
  object: SerializedObject
): object is BaseSerializedObject &
  (
    | IllustrationSerializedObject
    | BasicShapeSerializedObject
    | CombinedShapeSerializedObject
  ) {
  return (
    object.type === ObjectType.Illustration ||
    object.type === ObjectType.BasicShape ||
    object.type === ObjectType.CombinedShape
  );
}

export function isGuideSerializedObject(
  object: SerializedObject
): object is GuideSerializedObject {
  return object.type === ObjectType.Guide;
}

/**
 * Design state structure before multiple artboards
 */
export interface DesignStateSoleBoard extends DesignStateBase {
  artboardOptions: ArtboardOptionsWithBackgroundImage;
  overlayOptions?: TextureOptions | null;
  objects: SerializedObject[];
  groupStructure: StructureObject;
}

interface DesignStateSoleBoardForInit {
  config?: DesignStateSoleBoard['config'];
  artboardOptions: DesignStateSoleBoard['artboardOptions'];
  overlayOptions?: DesignStateSoleBoard['overlayOptions'];
  objects?: DesignStateSoleBoard['objects'];
  groupStructure?: DesignStateSoleBoard['groupStructure'];
}

/**
 * After multiple artboards, each serialized board will contain its own artboard, texture, and background image options.
 */

interface SerializeBoardBase {
  id: string;
  objects: SerializedObject[];
  groupStructure: StructureObject;
  // Used to indicate if an artboard is created from a template
  originTemplateId?: string;
}

export interface SerializedStandardBoard extends SerializeBoardBase {
  artboard: ArtboardOptions;
  title?: string;
  texture?: TextureOptions;
  backgroundImage?: BackgroundImageOptions;
}

export interface SerializedFloatingBoard extends SerializeBoardBase {
  floating: true;
}

export type SerializedBoard = SerializedStandardBoard | SerializedFloatingBoard;

export type UnfoldedSerializedBoard = Exclude<SerializedBoard, 'objects'> & {
  objects: SerializedObjectWithChildren[];
  unfoldedGroupStructure: StructureObject;
};

export const isSerializedFloatingBoard = <T extends SerializedBoard>(
  serializedBoard: T
): serializedBoard is T & SerializedFloatingBoard =>
  'floating' in serializedBoard;

export const isSerializedStandardBoard = <T extends SerializedBoard>(
  serializedBoard: T
): serializedBoard is T & SerializedStandardBoard =>
  !isSerializedFloatingBoard(serializedBoard);

/**
 * Design state structure after multiple artboards
 */
export interface DesignStateMultiBoard extends DesignStateBase {
  boards: SerializedBoard[];
}

export interface UnfoldedDesignStateMultiBoard extends DesignStateBase {
  boards: UnfoldedSerializedBoard[];
}

export interface DesignStateMultiBoardForInit {
  config?: DesignStateMultiBoard['config'];
  boards: SerializedBoard[];
}

export type DesignState = DesignStateSoleBoard | DesignStateMultiBoard;

export type DesignStateForInit =
  | DesignStateSoleBoardForInit
  | DesignStateMultiBoardForInit;

export interface MinSerializedObject {
  id: DesignObjectId;
  type: ObjectType;
}
